import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface UseScrollSpyOptions {
  scrollContainerRef: RefObject<HTMLElement | null>;
  sectionIds: string[];
}

// Active section is whichever is topmost above this line in the viewport.
const TRIGGER_LINE_RATIO = 0.3;
// Safety release for click-scroll suppression in case `scrollend` never
// fires (e.g. target already in view, or unsupported browser).
const CLICK_SCROLL_FALLBACK_MS = 1000;

export const useScrollSpy = ({
  scrollContainerRef,
  sectionIds,
}: UseScrollSpyOptions) => {
  const sectionRefs = useRef(new Map<string, HTMLElement>());
  const elementIds = useRef(new Map<HTMLElement, string>());
  const intersectingIds = useRef(new Set<string>());
  const refSetters = useRef(
    new Map<string, (node: HTMLElement | null) => void>()
  );
  const isClickScrolling = useRef(false);
  const releaseRef = useRef<(() => void) | null>(null);

  const [activeSectionId, setActiveSectionId] = useState<string>(
    sectionIds[0] ?? ''
  );

  const setSectionRef = useCallback((id: string) => {
    let setter = refSetters.current.get(id);
    if (!setter) {
      setter = (node: HTMLElement | null) => {
        if (node) {
          sectionRefs.current.set(id, node);
          elementIds.current.set(node, id);
        } else {
          sectionRefs.current.delete(id);
          intersectingIds.current.delete(id);
        }
      };
      refSetters.current.set(id, setter);
    }
    return setter;
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || sectionIds.length === 0) {
      return;
    }

    const pickActive = (entries: IntersectionObserverEntry[]) => {
      if (isClickScrolling.current) {
        return;
      }

      for (const { target, isIntersecting } of entries) {
        const id = elementIds.current.get(target as HTMLElement);
        intersectingIds.current[isIntersecting ? 'add' : 'delete'](id ?? '');
      }

      const next = sectionIds.findLast(id => intersectingIds.current.has(id));
      if (next) {
        setActiveSectionId(next);
      }
    };

    const observer = new IntersectionObserver(pickActive, {
      root: container,
      // Bottom margin pulls the active line up to TRIGGER_LINE_RATIO of the viewport.
      rootMargin: `0px 0px -${(1 - TRIGGER_LINE_RATIO) * 100}% 0px`,
      threshold: 0,
    });

    for (const el of sectionRefs.current.values()) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [scrollContainerRef, sectionIds]);

  const scrollToSection = (sectionId: string) => {
    const container = scrollContainerRef.current;
    const target = sectionRefs.current.get(sectionId);
    if (!container || !target) {
      return;
    }

    releaseRef.current?.();

    isClickScrolling.current = true;
    setActiveSectionId(sectionId);

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const release = () => {
      window.clearTimeout(timeoutId);
      container.removeEventListener('scrollend', release);
      isClickScrolling.current = false;
      releaseRef.current = null;
    };

    const timeoutId = window.setTimeout(release, CLICK_SCROLL_FALLBACK_MS);
    container.addEventListener('scrollend', release, { once: true });
    releaseRef.current = release;
  };

  return { activeSectionId, setSectionRef, scrollToSection };
};
